import type { ComponentProps } from "react";
import { useEffect, useRef, useState } from "react";
import { Box } from "@chakra-ui/react";
import { animate, motion } from "framer-motion";
import { ColorIcon, Spacing } from "goi_common";
import { BaseStack, baseColors } from "goi_common/src/design-system";

import { FOOD_STEP_SERVINGS } from "../utils/parlorIntroCost";
import { AFTER_BAR_RATIO } from "./ParlorCostIntro";
import type { CostConfig, CostItemKey, GuestRangeKey } from "../utils/parlorIntroCost";

const EASE = [0.22, 1, 0.36, 1] as const;
// 움직임 값: 레퍼런스 조사(scratchpad ref_motion, Material container transform · 분할 막대 80ms 간격) 기준
/** 카드 · 막대가 자리를 옮기는 시간. 0.45 → 0.7 → 1.0 → 1.15 → 1.05초. 위 작은 카드가 내려와 커지는 걸 눈으로 따라가게(2026-11-04 주완), 「진짜 조금만」 빠르게(11-05). 아래 지연들은 이 값에 맞춰 따라간다 */
const MOVE_S = 1.05;
/** 카드 이동이 거의 끝났을 때(94%) 막대가 나뉘기 시작한다 */
const SPLIT_DELAY_S = MOVE_S * 0.94;
/**
 * 카드가 내려와 커지는 움직임만 천천히 출발해 천천히 멈춘다. 빠르게 출발하는 EASE 로는 1초여도
 * 처음 0.3초에 거의 다 움직여 무엇이 내려왔는지 안 보였다(실측, 2026-11-04 주완)
 */
const MOVE_EASE = [0.45, 0, 0.25, 1] as const;

/** 조각 색. 크게 달라지는 항목은 주황 계열, 거의 정해진 항목은 회색 */
const SEGMENT_COLOR: Record<CostItemKey, string> = {
  food: baseColors.orange_600,
  binso: baseColors.primary_orange,
  altar: baseColors.orange_250,
  base: baseColors.gray_300,
};

/** 금액이 바뀌면 숫자가 굴러가듯 이어서 바뀐다(조문객 수 · 빈소를 바꿀 때 「움직이는」 느낌) */
function useRolling(value: number, reduceMotion: boolean) {
  const [shown, setShown] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    if (reduceMotion) {
      from.current = value;
      setShown(value);
      return;
    }
    // 바꿀 때 바로 따라오게 짧게(주완: 느리다)
    const controls = animate(from.current, value, {
      duration: 0.25,
      ease: "easeOut",
      onUpdate: (v) => {
        from.current = v;
        setShown(Math.round(v / 10) * 10);
      },
    });
    return () => controls.stop();
  }, [value, reduceMotion]);
  return shown;
}

/** 조문객 단계. 질문 화면 선택지와 같은 값 */
const GUEST_STEPS: { value: GuestRangeKey; label: string; count: number }[] = [
  { value: "0명(무빈소)", label: "없음", count: 0 },
  { value: "50명 내외", label: "50명", count: 50 },
  { value: "100명 내외", label: "100명", count: 100 },
  { value: "200명 이상", label: "200명+", count: 200 },
];

const pyeongText = (p: number) => `${p}평`;

/** 빈소 평형 − / + 버튼 */
function StepButton({
  label,
  disabled,
  onClick,
  children,
  bg,
}: {
  label: string;
  disabled: boolean;
  onClick: () => void;
  children: string;
  bg: string;
}) {
  return (
    <Box
      as="button"
      type="button"
      aria-label={label}
      cursor={disabled ? "default" : "pointer"}
      width="24px"
      height="24px"
      borderRadius="999px"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
      bg={bg}
      color={disabled ? baseColors.label_muted_1 : baseColors.label_normal}
      onClick={disabled ? undefined : onClick}
    >
      <Box as="span" fontSize="16px" fontWeight={600} lineHeight={1}>
        {children}
      </Box>
    </Box>
  );
}

/** 처음 제목은 카드가 거의 자리 잡을 즈음 나타난다. 다 멈춘 뒤면 늦게 느껴졌다(11-05 주완) */
const TITLE_DELAY_S = MOVE_S - 0.05;

/** 항목 그림. 고이 디자인 시스템 Graphic(이모지 결). 밥그릇 · 국화는 시스템에 없어 같은 결로 새로 그림 */
const DRIVER_ICON: Record<CostItemKey, ComponentProps<typeof ColorIcon>["name"]> = {
  food: "icon_Graphic_RiceBowl",
  binso: "icon_Graphic_Building",
  altar: "icon_Graphic_Chrysanthemum",
  base: "icon_Graphic_BlackRibbon",
};

/** 오른쪽 − 값 + . 값 칸 너비를 고정해 숫자가 바뀌어도 버튼이 움직이지 않는다 */
function Stepper({
  name,
  value,
  canDown,
  canUp,
  onDown,
  onUp,
  onGray = false,
}: {
  /** 회색 박스 안이면 버튼을 흰색으로(회색 위 회색이라 안 보였다) */
  onGray?: boolean;
  name: string;
  value: string;
  canDown: boolean;
  canUp: boolean;
  onDown: () => void;
  onUp: () => void;
}) {
  return (
    <BaseStack direction="horizontal" alignItems="center">
      <StepButton
        label={`${name} 줄이기`}
        disabled={!canDown}
        onClick={onDown}
        bg={onGray ? baseColors.background_normal : baseColors.fill_normal}
      >
        −
      </StepButton>
      <Box
        className="body_2_normal_sb"
        color={baseColors.label_normal}
        width="60px"
        textAlign="center"
        whiteSpace="nowrap"
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {value}
      </Box>
      <StepButton
        label={`${name} 늘리기`}
        disabled={!canUp}
        onClick={onUp}
        bg={onGray ? baseColors.background_normal : baseColors.fill_normal}
      >
        +
      </StepButton>
    </BaseStack>
  );
}

/** 항목 상태 태그: 좋음(적정 · 넉넉 · 여유) · 주의(부족 · 많음 · 작음 · 큼) · 매우(매우 부족 · 매우 많음 · 매우 작음 · 매우 큼) */
type Tone = "good" | "warn" | "danger";
type Tag = { text: string; tone: Tone };
const TONE_STYLE: Record<Tone, { bg: string; color: string }> = {
  good: { bg: baseColors.orange_100, color: baseColors.orange_600 },
  warn: { bg: baseColors.red_100, color: baseColors.red_600 },
  // 「매우 ○○」 는 한 단계 더 진하게
  danger: { bg: baseColors.red_150, color: baseColors.red_700 },
};

/** 항목 이름 · 오른쪽 이름(범례와 같은 말) */
const LEGEND_LABEL: Record<CostItemKey, string> = {
  food: "식사",
  binso: "빈소",
  altar: "제단꽃",
  base: "기타",
};

/** 「지금 예상 → 맞춤 견적 후」 비교 막대 높이(px). 오른쪽 비율은 첫 화면과 같은 AFTER_BAR_RATIO */
const AFTER_BAR_MAX = 120;
/** 위 박스 범례까지 다 나온 뒤(범례 = SPLIT_DELAY_S + 0.4 에서 0.4초) 두 번째 제목, 그다음 아래 박스 */
const SAVE_TITLE_DELAY_S = SPLIT_DELAY_S + 0.85;
const LOWER_BOX_DELAY_S = SAVE_TITLE_DELAY_S + 0.5;

/**
 * E5 ④ 비용 구성 「예상 비용은 이렇게 나뉘어요」(2026-11-05 주완).
 *   질문 화면의 작은 비용 카드(layoutId e5-cost-card)가 이 자리에서 커지고, 주황 막대가 질문 화면 구간 자리에서
 *   꽉 찬 막대로 늘어난 뒤 항목 조각으로 나뉜다. 막대 아래 범례로 색이 무엇인지 알려 준다.
 * 보기 전용(adjustable=false, 지금): 박스 아래 「상황에 맞춰 준비하면 / 더 줄어들 수 있어요」 + 지금 예상 → 맞춤 견적 후 막대.
 *   첫 화면 「견적 받기 전 / 맞춤 견적 후」 약속을 여기서 잇는다.
 * 직접 맞춰 보기(adjustable=true, 나중 small hook 실험용으로 보관): 조문객 회색 박스 + 식사(30인분씩) · 빈소 평형 · 제단꽃 등급 − +,
 *   바꾸면 예상 비용과 막대가 그 자리에서 움직인다(e5_set_guest · food · binso · altar). 조문객이 없으면 식사 · 빈소 · 제단꽃은 흐려진다.
 */
export default function CompositionCard({
  config,
  guest,
  startSegment,
  reduceMotion,
  onGuest,
  onBinso,
  onAltar,
  onFood,
  adjustable,
}: {
  /**
   * true: 고객이 직접 − + 로 맞춰 보는 화면(small hook 실험용, 보관 — goi-search prototypes/e5-cost-configurator).
   * false: 보기 전용. 항목마다 어떻게 줄일 수 있는지만 짧게, 박스 아래 제목 「맞춤 견적을 받으면 더 줄어들 수 있어요」 + 지금 예상 → 맞춤 견적 후 막대(2026-11-05 주완)
   */
  adjustable: boolean;
  config: CostConfig;
  guest: GuestRangeKey;
  /** 질문 화면 막대에서 주황 구간이 있던 자리(%). 여기서 출발해 꽉 찬 막대가 된다 */
  startSegment: { left: number; width: number };
  reduceMotion: boolean;
  onGuest: (value: GuestRangeKey) => void;
  onBinso: (pyeong: number) => void;
  onAltar: (index: number) => void;
  onFood: (servings: number) => void;
}) {
  const lo = useRolling(config.lo, reduceMotion);
  const hi = useRolling(config.hi, reduceMotion);
  const { binso, altar, food } = config;
  const binsoIndex = binso ? binso.pyeongs.indexOf(binso.pyeong) : -1;
  const guestPos = GUEST_STEPS.findIndex((g) => g.value === guest);
  const rowsDelay = (i: number) => (reduceMotion ? 0 : MOVE_S + 0.15 + i * 0.08);

  type Row = {
    key: string;
    icon: ComponentProps<typeof ColorIcon>["name"];
    label: string;
    desc: string;
    /** 이름 옆 상태 태그 */
    tag?: Tag;
    off?: boolean;
    right: React.ReactNode;
  };
  const muted = (text: string) => (
    <Box className="label_1_r" color={baseColors.label_assistive_1}>
      {text}
    </Box>
  );
  const rightText = (text: string) => (
    <Box className="body_2_normal_sb" color={baseColors.label_alternative_1} whiteSpace="nowrap">
      {text}
    </Box>
  );

  // 상태는 이름 옆 두 글자 태그, 아래 줄은 늘 「무엇인지」 설명(주완). 좋은 상태는 주황, 주의는 빨강
  const binsoTag = (): Tag | undefined => {
    if (!binso) return undefined;
    // 추천에서 몇 칸 떨어졌는지: −2 이하 매우 작음 · −1 작음 · 0 적정 · +1 여유 · +2 큼 · +3 이상 매우 큼
    const d = binso.pyeongs.indexOf(binso.pyeong) - binso.pyeongs.indexOf(binso.recommended);
    if (d <= -2) return { text: "매우 작음", tone: "danger" };
    if (d === -1) return { text: "작음", tone: "warn" };
    if (d === 0) return { text: "적정", tone: "good" };
    if (d === 1) return { text: "여유", tone: "good" };
    if (d === 2) return { text: "큼", tone: "warn" };
    return { text: "매우 큼", tone: "danger" };
  };
  // 식사: 조문객 수만큼이 적정. 30인분 넘게 모자라면 매우 부족, 30인분까지 더 많으면 넉넉, 60인분까지 많음, 그 위 매우 많음
  const foodTag = (): Tag | undefined => {
    if (!food) return undefined;
    const d = food.servings - food.base;
    if (d < -FOOD_STEP_SERVINGS) return { text: "매우 부족", tone: "danger" };
    if (d < 0) return { text: "부족", tone: "warn" };
    if (d === 0) return { text: "적정", tone: "good" };
    if (d <= FOOD_STEP_SERVINGS) return { text: "넉넉", tone: "good" };
    if (d <= FOOD_STEP_SERVINGS * 2) return { text: "많음", tone: "warn" };
    return { text: "매우 많음", tone: "danger" };
  };
  // 제단꽃은 태그 없음. 실제 공시가는 호수(1호 · 2호 · 특1호)로만 나뉘어 「깔끔 · 화려」 같은 설명은 사실이 아니었다

  // 식사 단계: 조문객 수에서 30인분씩, 가장 적게는 50인분(그 아래는 거의 없다). 70 → 50 처럼 마지막 칸은 50 으로(주완)
  const foodSteps = food
    ? Array.from(
        new Set([
          50,
          ...Array.from({ length: 12 }, (_, k) => food.base + (k - 6) * FOOD_STEP_SERVINGS).filter(
            (v) => v > 50 && v <= food.base + FOOD_STEP_SERVINGS * 5,
          ),
        ]),
      ).sort((x, y) => x - y)
    : [];

  const foodIndex = food ? foodSteps.indexOf(food.servings) : -1;

  const item = (k: CostItemKey) => config.items.find((i) => i.key === k);
  const rows: Row[] = [
    {
      key: "guest",
      icon: "icon_Graphic_Suit",
      label: "조문객",
      desc: "오시는 분 수",
      right: (
        <Stepper
          onGray
          name="조문객"
          value={GUEST_STEPS[guestPos]?.label ?? ""}
          canDown={guestPos > 0}
          canUp={guestPos < GUEST_STEPS.length - 1}
          onDown={() => onGuest(GUEST_STEPS[guestPos - 1].value)}
          onUp={() => onGuest(GUEST_STEPS[guestPos + 1].value)}
        />
      ),
    },
    {
      key: "food",
      icon: DRIVER_ICON.food,
      label: "식사",
      desc: "조문객 식사와 음료",
      tag: foodTag(),
      off: item("food")?.off,
      right: food ? (
        <Stepper
          name="식사"
          value={`${food.servings}인분`}
          canDown={foodIndex > 0}
          canUp={foodIndex < foodSteps.length - 1}
          onDown={() => onFood(foodSteps[foodIndex - 1])}
          onUp={() => onFood(foodSteps[foodIndex + 1])}
        />
      ) : (
        muted("필요 없어요")
      ),
    },
    {
      key: "binso",
      icon: DRIVER_ICON.binso,
      label: "빈소",
      desc: "조문객을 맞이하는 공간",
      tag: binsoTag(),
      off: item("binso")?.off,
      right: binso ? (
        <Stepper
          name="빈소"
          value={pyeongText(binso.pyeong)}
          canDown={binsoIndex > 0}
          canUp={binsoIndex < binso.pyeongs.length - 1}
          onDown={() => onBinso(binso.pyeongs[binsoIndex - 1])}
          onUp={() => onBinso(binso.pyeongs[binsoIndex + 1])}
        />
      ) : (
        muted("필요 없어요")
      ),
    },
    {
      key: "altar",
      icon: DRIVER_ICON.altar,
      label: "제단꽃",
      desc: "빈소를 꾸미는 꽃",
      off: item("altar")?.off,
      right: altar ? (
        altar.labels.length > 1 ? (
          <Stepper
            name="제단꽃"
            value={altar.labels[altar.index]}
            canDown={altar.index > 0}
            canUp={altar.index < altar.labels.length - 1}
            onDown={() => onAltar(altar.index - 1)}
            onUp={() => onAltar(altar.index + 1)}
          />
        ) : (
          rightText(altar.labels[0])
        )
      ) : (
        muted("필요 없어요")
      ),
    },
    {
      key: "base",
      icon: DRIVER_ICON.base,
      label: "기타",
      desc: "안치 · 입관 등 기본 비용",
      right: muted("고정된 요금"),
    },
  ];

  const [guestRow, ...itemRows] = rows;

  return (
    <BaseStack width="100%" px="20px">
      <Spacing gap={20} />
      <motion.div
        initial={reduceMotion ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: reduceMotion ? 0 : TITLE_DELAY_S, duration: 0.34, ease: EASE }}
      >
        <Box as="h2" className="title_3_b" color={baseColors.label_normal} wordBreak="keep-all">
          예상 비용은 이렇게 나뉘어요
        </Box>
      </motion.div>
      <Spacing gap={20} />

      <motion.div
        layoutId="e5-cost-card"
        transition={{ duration: reduceMotion ? 0 : MOVE_S, ease: MOVE_EASE }}
        style={{ borderRadius: 16, background: baseColors.orange_100, overflow: "hidden" }}
      >
        <Box px="20px" pt="24px" pb="20px">
          <Box className="label_1_r" color={baseColors.label_alternative_1}>
            우리 가족 예상 비용
          </Box>
          <Spacing gap={4} />
          <Box
            className="title_2_b"
            color={baseColors.primary_orange}
            sx={{ fontVariantNumeric: "tabular-nums" }}
          >
            약 {lo.toLocaleString()}~{hi.toLocaleString()}만 원
          </Box>
          <Spacing gap={18} />

          {/* 질문 화면의 주황 구간이 내려와 꽉 찬 막대가 되고, 그 막대가 항목 조각으로 나뉜다 */}
          <Box position="relative" width="100%" height="12px">
            {/* 질문 화면에서 보던 주황 구간(같은 위치 · 길이)에서 시작해 꽉 찬 막대로 늘어난 뒤, 조각에 덮이며 사라진다 */}
            <motion.div
              initial={
                reduceMotion
                  ? false
                  : { left: `${startSegment.left}%`, width: `${startSegment.width}%`, opacity: 1 }
              }
              animate={{ left: "0%", width: "100%", opacity: [1, 1, 0] }}
              transition={{
                left: { duration: reduceMotion ? 0 : MOVE_S, ease: MOVE_EASE },
                width: { duration: reduceMotion ? 0 : MOVE_S, ease: MOVE_EASE },
                opacity: { duration: reduceMotion ? 0 : SPLIT_DELAY_S + 0.6, times: [0, 0.8, 1] },
              }}
              style={{
                position: "absolute",
                top: 0,
                bottom: 0,
                borderRadius: 999,
                background: baseColors.primary_orange,
              }}
            />
            <Box position="absolute" inset={0} display="flex">
              {config.items.map((d, i) => (
                // 설정을 바꾸면 조각 길이만 부드럽게 바뀐다(flex-grow CSS 전환. framer 로는 flexGrow 가 움직이지 않았다)
                <Box
                  key={d.key}
                  flexGrow={d.off ? 0.0001 : Math.max(d.share, 0.05)}
                  flexBasis={0}
                  minW={0}
                  height="100%"
                  pr={i < config.items.length - 1 && !d.off ? "3px" : 0}
                  transition={reduceMotion ? undefined : "flex-grow 0.25s ease-out"}
                >
                  <motion.div
                    initial={reduceMotion ? false : { opacity: 0, scaleX: 0.6 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    transition={{
                      delay: reduceMotion ? 0 : SPLIT_DELAY_S + i * 0.12,
                      duration: 0.36,
                      ease: EASE,
                    }}
                    style={{
                      width: "100%",
                      height: "100%",
                      borderRadius: 999,
                      background: SEGMENT_COLOR[d.key],
                      transformOrigin: "left center",
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Box>

          {/* 범례: 색이 무엇인지. 항목 줄마다 점을 두는 것보다 한곳에서 보는 게 쉽다(주완) */}
          <motion.div
            initial={reduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: reduceMotion ? 0 : SPLIT_DELAY_S + 0.4, duration: 0.4 }}
          >
            <BaseStack
              direction="horizontal"
              justifyContent="flex-start"
              flexWrap="wrap"
              pt="12px"
              spacing={12}
            >
              {config.items.map((d) => (
                <BaseStack
                  key={d.key}
                  direction="horizontal"
                  alignItems="center"
                  opacity={d.off ? 0.35 : 1}
                >
                  <Box
                    width="8px"
                    height="8px"
                    borderRadius="999px"
                    flexShrink={0}
                    bg={SEGMENT_COLOR[d.key]}
                  />
                  <Spacing gap={4} orientation="horizontal" />
                  <Box className="caption_1_r" color={baseColors.label_alternative_1}>
                    {LEGEND_LABEL[d.key]}
                  </Box>
                </BaseStack>
              ))}
            </BaseStack>
          </motion.div>
        </Box>
      </motion.div>

      {/* 보기 전용: 비용 박스 아래 새 박스. 첫 화면 「맞춤 견적 후」 약속을 크게 이어받고, 어떻게 줄어드는지 한마디씩 돌아간다.
          항목 줄(식사 · 빈소 · 제단꽃 · 기타)은 위 범례로 충분해 두지 않는다(2026-11-05 주완) */}
      {!adjustable && (
        <>
          {/*
            나오는 순서: 위 박스(금액 · 막대 · 범례)가 다 나온 뒤 → 제목 → 아래 박스(주완). 화살표는 뺐다.
            「상황에 맞춰 준비하면 더 줄어들 수 있어요」(맞춤 견적을 받으면 → 요청하는 느낌, 고르면 → 어색해서 바꿈, 주완) 는 박스 밖, 페이지 제목과 같은 크기(title_3_b)
          */}
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: SAVE_TITLE_DELAY_S, duration: 0.4, ease: EASE }}
          >
            <Spacing gap={36} />
            <Box as="h3" className="title_3_b" color={baseColors.label_normal} wordBreak="keep-all">
              상황에 맞춰 준비하면
              <br />
              <Box as="span" color={baseColors.primary_orange}>
                더 줄어들 수 있어요
              </Box>
            </Box>
            <Spacing gap={16} />
          </motion.div>
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: LOWER_BOX_DELAY_S, duration: 0.45, ease: EASE }}
          >
            {/*
            레퍼런스 A(자산 앱 「주담대 이자 절약」 현재 vs 대환하면 「?」, UIBowl 2026-11-05): 지금 예상 → 맞춤 견적 후 막대 두 개.
            첫 화면 「견적 받기 전 / 맞춤 견적 후」 막대와 같은 모양 · 색이라 첫 화면 약속을 여기서 회수한다(주완)
          */}
            <Box bg={baseColors.fill_normal} borderRadius="16px" px="16px" pt="24px" pb="20px">
              <BaseStack
                direction="horizontal"
                width="100%"
                justifyContent="center"
                alignItems="flex-end"
                spacing={28}
              >
                {/* 왼쪽: 지금 예상(회색, 위쪽 빗금 = 줄어들 수 있는 몫) */}
                <BaseStack alignItems="center" width="120px">
                  <BaseStack
                    width="56px"
                    height={`${AFTER_BAR_MAX}px`}
                    borderRadius="8px"
                    overflow="hidden"
                  >
                    <Box
                      height={`${Math.round(AFTER_BAR_MAX * (1 - AFTER_BAR_RATIO))}px`}
                      background={`repeating-linear-gradient(135deg, ${baseColors.orange_150} 0 4px, ${baseColors.orange_100} 4px 8px)`}
                    />
                    <Box flex={1} bg={baseColors.gray_150} />
                  </BaseStack>
                  <Spacing gap={10} />
                  <Box
                    className="label_1_sb"
                    color={baseColors.label_alternative_1}
                    whiteSpace="nowrap"
                    sx={{ fontVariantNumeric: "tabular-nums" }}
                  >
                    약 {lo.toLocaleString()}~{hi.toLocaleString()}만 원
                  </Box>
                  <Box className="caption_1_r" color={baseColors.label_assistive_1}>
                    지금 예상
                  </Box>
                </BaseStack>
                {/* 오른쪽: 맞춤 견적 후(주황, 금액은 ??? 로 가림). 왼쪽 다음에 자라 올라온다 */}
                <BaseStack alignItems="center" width="120px">
                  <Box
                    className="label_2_sb"
                    color={baseColors.primary_orange}
                    bg={baseColors.orange_100}
                    px="8px"
                    py="4px"
                    borderRadius="6px"
                  >
                    -???만 원
                  </Box>
                  <Spacing gap={8} />
                  <motion.div
                    initial={reduceMotion ? false : { height: 0 }}
                    animate={{ height: Math.round(AFTER_BAR_MAX * AFTER_BAR_RATIO) }}
                    transition={{ delay: LOWER_BOX_DELAY_S + 0.3, duration: 0.6, ease: MOVE_EASE }}
                    style={{ width: 56, borderRadius: 8, background: baseColors.primary_orange }}
                  />
                  <Spacing gap={10} />
                  <Box className="label_1_sb" color={baseColors.primary_orange} fontWeight={700}>
                    ???만 원
                  </Box>
                  <Box className="caption_1_r" color={baseColors.label_assistive_1}>
                    맞춤 견적 후
                  </Box>
                </BaseStack>
              </BaseStack>
            </Box>
          </motion.div>
        </>
      )}

      {/* 조문객은 회색 박스로 따로. 아래 항목들을 정하는 기준이라 구분되게(주완) */}
      {adjustable ? (
        <>
          <Spacing gap={16} />
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: rowsDelay(0), duration: 0.4, ease: EASE }}
          >
            <BaseStack
              direction="horizontal"
              width="100%"
              alignItems="center"
              bg={baseColors.fill_normal}
              borderRadius="12px"
              px="16px"
              py="14px"
            >
              <ColorIcon name={guestRow.icon} size={32} />
              <Spacing gap={12} orientation="horizontal" />
              <BaseStack flex={1} minW={0}>
                <Box className="body_1_normal_sb" color={baseColors.label_normal}>
                  {guestRow.label}
                </Box>
                <Spacing gap={2} />
                <Box className="label_1_r" color={baseColors.label_assistive_1} whiteSpace="nowrap">
                  {guestRow.desc}
                </Box>
              </BaseStack>
              <Spacing gap={8} orientation="horizontal" />
              <Box flexShrink={0}>{guestRow.right}</Box>
            </BaseStack>
          </motion.div>
        </>
      ) : null}

      {/* 항목 줄(식사 · 빈소 · 제단꽃 · 기타)은 직접 맞춰 보기에서만. 보기 전용은 위 범례로 충분하다(주완) */}
      {adjustable && (
        <>
          <Spacing gap={4} />
          <BaseStack width="100%">
            {itemRows.map((r, i) => (
              <motion.div
                key={r.key}
                initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: rowsDelay(i + 1), duration: 0.4, ease: EASE }}
              >
                {/* 위 조문객 박스 안쪽(16px)과 그림 · − + 위치가 세로로 맞게 같은 만큼 들인다 */}
                <BaseStack
                  direction="horizontal"
                  width="100%"
                  px="16px"
                  py="14px"
                  alignItems="center"
                  // 조문객 없음이면 바로 흐려진다(전환 없음, 주완)
                  opacity={r.off ? 0.4 : 1}
                  borderBottom={
                    i < itemRows.length - 1 ? `1px solid ${baseColors.line_alternative}` : undefined
                  }
                >
                  <ColorIcon name={r.icon} size={32} />
                  <Spacing gap={12} orientation="horizontal" />
                  <BaseStack flex={1} minW={0}>
                    <BaseStack direction="horizontal" alignItems="center">
                      <Box className="body_1_normal_sb" color={baseColors.label_normal}>
                        {r.label}
                      </Box>
                      {r.tag && (
                        <>
                          <Spacing gap={6} orientation="horizontal" />
                          <Box
                            className="caption_1_sb"
                            px="6px"
                            py="2px"
                            borderRadius="4px"
                            whiteSpace="nowrap"
                            bg={TONE_STYLE[r.tag.tone].bg}
                            color={TONE_STYLE[r.tag.tone].color}
                          >
                            {r.tag.text}
                          </Box>
                        </>
                      )}
                    </BaseStack>
                    <Spacing gap={2} />
                    {/* 설명은 한 줄. 넘치면 말줄임(두 줄 금지, 주완) */}
                    <Box
                      className="label_1_r"
                      color={baseColors.label_assistive_1}
                      whiteSpace="nowrap"
                      overflow="hidden"
                      textOverflow="ellipsis"
                    >
                      {r.desc}
                    </Box>
                  </BaseStack>
                  <Spacing gap={8} orientation="horizontal" />
                  <Box flexShrink={0}>{r.right}</Box>
                </BaseStack>
              </motion.div>
            ))}
          </BaseStack>
        </>
      )}
    </BaseStack>
  );
}
